import { ApiError } from '../work/gateway.js';

/**
 * The browser's calls to Party Master (FR-PTY-001, FR-PTY-002, FR-PTY-005).
 *
 * SAME THREE RULES AS EVERY OTHER GATEWAY. Nothing here names a tenant. Nothing
 * here filters or hides — what comes back has already been through
 * `accessPredicate` in SQL. And nothing here DECIDES: whether two organisations
 * are the same one is the server's answer, computed from normalised
 * registration numbers and a trigram index, and a browser that guessed would
 * be guessing about whose invoice goes to whom.
 *
 * IT CARRIES THE CATALOGUE TOO, because both are Platform Core master data
 * with the same shape of problem: a tenant could not add a customer OR a thing
 * to sell them, since neither had a route. Keeping them in one gateway keeps
 * the browser's idea of "shared records" in one file rather than splitting it
 * by which package happens to own the table.
 *
 * THE DUPLICATE CHECK IS A CONVENIENCE HERE AND A CONTROL THERE. This calls
 * `/parties/duplicates` so somebody sees the candidates before they finish
 * typing; the create call is refused by the SERVER with the same candidates if
 * they do not. A screen that only warned would be a screen that could be
 * skipped.
 */

export interface Party {
  readonly id: string;
  readonly partyKind: 'organisation' | 'person';
  readonly displayName: string;
  readonly status: string;
  readonly mergedIntoId: string | null;
  readonly version: number;
}

export interface DuplicateCandidate {
  readonly id: string;
  readonly displayName: string;
  readonly partyKind?: string;
  readonly score: number;
  readonly reason: string;
}

/** FR-PRD-001. What a tenant sells and buys, shared across modules. */
export interface CatalogueItem {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly itemKind: 'product' | 'service';
  readonly unit: string;
  readonly isActive: boolean;
}

export interface PartiesGateway {
  parties(filter: { role?: 'customer' | 'supplier'; search?: string }): Promise<readonly Party[]>;
  duplicates(displayName: string): Promise<readonly DuplicateCandidate[]>;
  registerParty(input: {
    partyKind: 'organisation' | 'person';
    displayName: string;
    identifiers?: readonly { kind: 'vat_number' | 'company_number'; value: string }[];
    roles?: readonly ('customer' | 'supplier')[];
    notADuplicateOf?: readonly string[];
  }): Promise<Party>;
  addRole(id: string, role: 'customer' | 'supplier'): Promise<Party>;
  merge(survivingPartyId: string, losingPartyId: string): Promise<Party>;
  catalogueItems(search?: string): Promise<readonly CatalogueItem[]>;
  addCatalogueItem(input: {
    code: string;
    name: string;
    itemKind: 'product' | 'service';
    unit?: string;
  }): Promise<CatalogueItem>;
}

function newKey(): string {
  return crypto.randomUUID();
}

export function createPartiesGateway(baseUrl = '/api/v1'): PartiesGateway {
  async function call<T>(path: string, init?: RequestInit & { readonly key?: string }): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (init?.key !== undefined) headers['idempotency-key'] = init.key;

    const response = await fetch(`${baseUrl}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
    });

    if (!response.ok) {
      const problem = (await response.json().catch(() => ({}))) as {
        title?: string;
        detail?: string;
      };
      throw new ApiError(response.status, problem.title ?? 'Something went wrong', problem.detail);
    }
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }

  const post = <T>(path: string, body: unknown): Promise<T> =>
    call<T>(path, { method: 'POST', key: newKey(), body: JSON.stringify(body) });

  return {
    async parties(filter) {
      const query = new URLSearchParams();
      if (filter.role !== undefined) query.set('role', filter.role);
      if (filter.search !== undefined && filter.search !== '') query.set('search', filter.search);
      const suffix = query.toString();
      const body = await call<{ parties: readonly Party[] }>(
        `/parties${suffix === '' ? '' : `?${suffix}`}`,
      );
      return body.parties;
    },
    async duplicates(displayName) {
      const body = await call<{ candidates: readonly DuplicateCandidate[] }>(
        `/parties/duplicates?displayName=${encodeURIComponent(displayName)}`,
      );
      return body.candidates;
    },
    async registerParty(input) {
      const body = await post<{ party: Party }>('/parties', input);
      return body.party;
    },
    async addRole(id, role) {
      const body = await post<{ party: Party }>(`/parties/${encodeURIComponent(id)}/roles`, {
        role,
      });
      return body.party;
    },
    async catalogueItems(search) {
      const suffix =
        search === undefined || search === '' ? '' : `?search=${encodeURIComponent(search)}`;
      const body = await call<{ items: readonly CatalogueItem[] }>(`/catalogue/items${suffix}`);
      return body.items;
    },
    async addCatalogueItem(input) {
      const body = await post<{ item: CatalogueItem }>('/catalogue/items', input);
      return body.item;
    },
    async merge(survivingPartyId, losingPartyId) {
      const body = await post<{ party: Party }>(
        `/parties/${encodeURIComponent(survivingPartyId)}/merge`,
        { losingPartyId },
      );
      return body.party;
    },
  };
}
